// confirmDialog — brand-konsistenter Bestaetigungs-Dialog fuer destruktive/
// unwiderrufliche Aktionen. Baut auf dem generischen openModal auf (PFS-584):
// Titel im Modal-Header, Icon + Message im Body, zwei Aktionen im Footer.
//
// Zwei Modi:
//   1. Ohne onConfirm (back-compat, die bestehenden Aufrufstellen):
//        const ok = await confirmDialog({ title, message, variant: "danger" });
//        if (ok) { await doDelete(); }        // Arbeit AUSSERHALB
//   2. Mit onConfirm: die Arbeit laeuft IM Dialog (Button busy), er schliesst
//      erst nach Erfolg, bleibt bei Fehler offen mit Status-Zeile:
//        await confirmDialog({ title, message, variant: "danger",
//          confirmLabel: "Loeschen", busyLabel: "Loesche...",
//          onConfirm: () => api.deletePractice(id) });
//
// Brand-agnostisch (nur --pfs-*-Tokens; mednet = gruen, pfs = blau).
import { escapeHTML } from "../../utils/escape-html.js";
import { openModal, DialogResult } from "../modal/open-modal.js";

export interface ConfirmDialogOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "primary" | "danger";
  // Optional: laeuft IM Dialog (Button busy) BEVOR er schliesst. Wirft er,
  // bleibt der Dialog offen und zeigt eine Fehler-Status-Zeile.
  onConfirm?: () => void | Promise<void>;
  busyLabel?: string;
}

const WARN_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>';

const CONFIRM_CSS_ID = "pfs-confirm-dialog-styles";
function ensureConfirmStyles(): void {
  if (document.getElementById(CONFIRM_CSS_ID)) return;
  const style = document.createElement("style");
  style.id = CONFIRM_CSS_ID;
  style.textContent = `
.pfs-cd-alert { display: flex; gap: 1rem; align-items: center; }
.pfs-cd-icon { flex-shrink: 0; width: 2.5rem; height: 2.5rem; border-radius: 9999px; display: flex; align-items: center; justify-content: center; background: var(--pfs-color-danger-container); color: var(--pfs-color-on-danger-container); }
.pfs-cd-icon svg { width: 1.4rem; height: 1.4rem; }
.pfs-cd-message { margin: 0; font-size: 0.9rem; line-height: 1.5; color: var(--pfs-color-on-surface-muted); }
`;
  document.head.appendChild(style);
}

/**
 * Oeffnet einen Bestaetigungs-Dialog und resolved mit `true` (bestaetigt) /
 * `false` (abgebrochen). Mit `onConfirm` laeuft die Arbeit im Dialog.
 */
export function confirmDialog(opts: ConfirmDialogOptions): Promise<boolean> {
  ensureConfirmStyles();
  const variant = opts.variant === "danger" ? "danger" : "primary";
  const iconHTML = variant === "danger" ? `<div class="pfs-cd-icon">${WARN_ICON}</div>` : "";
  const content = `<div class="pfs-cd-alert">${iconHTML}<p class="pfs-cd-message">${escapeHTML(opts.message)}</p></div>`;

  const confirmAction = opts.onConfirm
    ? async (): Promise<DialogResult<boolean>> => {
        try {
          await opts.onConfirm!();
          return DialogResult.close(true);
        } catch (err) {
          console.error("[confirmDialog] onConfirm error", err);
          return DialogResult.keepOpen({ text: "Aktion fehlgeschlagen", tone: "error" });
        }
      }
    : (): DialogResult<boolean> => DialogResult.close(true);

  return openModal<boolean>({
    title: opts.title,
    size: "sm",
    content,
    dismissValue: false,
    actions: [
      { label: opts.cancelLabel ?? "Abbrechen", variant: "ghost", onClick: () => DialogResult.close(false) },
      {
        label: opts.confirmLabel ?? "Bestätigen",
        variant,
        busyLabel: opts.busyLabel,
        onClick: confirmAction,
      },
    ],
  });
}
